import { CompositeFacet, FacetGroup, FacetSelections, ScalarFacet, TallyResult } from './facet-predicate.types';

export function facetKeys<TRow>(group: FacetGroup<TRow>): string[] {
	return group.kind === 'scalar' ? [group.key] : group.keys;
}

function asArray(value: number | number[] | null): number[] {
	if (value === null || value === undefined) {
		return [];
	}
	return Array.isArray(value) ? value : [value];
}

function selectionFor(selections: FacetSelections, key: string): number[] {
	return selections[key] ?? [];
}

function scalarMatches<TRow>(facet: ScalarFacet<TRow>, row: TRow, selections: FacetSelections): boolean {
	const selected = selectionFor(selections, facet.key);
	if (selected.length === 0) {
		return true;
	}
	return asArray(facet.valuesOf(row)).some((v) => selected.includes(v));
}

function tupleMatchesKey<TRow, TTuple>(facet: CompositeFacet<TRow, TTuple>, tuple: TTuple, key: string, selections: FacetSelections): boolean {
	const selected = selectionFor(selections, key);
	if (selected.length === 0) {
		return true;
	}
	const value = facet.valuesOf[key](tuple);
	return value !== null && value !== undefined && selected.includes(value);
}

function compositeMatches<TRow, TTuple>(facet: CompositeFacet<TRow, TTuple>, row: TRow, selections: FacetSelections): boolean {
	// Nothing selected in any of this group's keys: the group filters nothing,
	// including rows that carry no tuples at all.
	if (facet.keys.every((k) => selectionFor(selections, k).length === 0)) {
		return true;
	}
	return facet.tuples(row).some((t) => facet.keys.every((k) => tupleMatchesKey(facet, t, k, selections)));
}

function groupMatches<TRow>(group: FacetGroup<TRow>, row: TRow, selections: FacetSelections): boolean {
	return group.kind === 'scalar' ? scalarMatches(group, row, selections) : compositeMatches(group, row, selections);
}

function bump(counts: Map<number, number>, value: number): void {
	counts.set(value, (counts.get(value) ?? 0) + 1);
}

function bumpGroup<TRow>(group: FacetGroup<TRow>, row: TRow, selections: FacetSelections, counts: { [filterKey: string]: Map<number, number> }): void {
	if (group.kind === 'scalar') {
		// Distinct, so a row belonging twice to one option still counts once.
		for (const value of new Set(asArray(group.valuesOf(row)))) {
			bump(counts[group.key], value);
		}
		return;
	}

	const tuples = group.tuples(row);

	for (const key of group.keys) {
		const others = group.keys.filter((k) => k !== key);
		const seen = new Set<number>();

		for (const tuple of tuples) {
			// Within a composite group a tuple must satisfy the group's OTHER keys.
			// This is what keeps same-student correlation intact.
			if (!others.every((k) => tupleMatchesKey(group, tuple, k, selections))) {
				continue;
			}
			const value = group.valuesOf[key](tuple);
			if (value !== null && value !== undefined) {
				seen.add(value);
			}
		}

		for (const value of seen) {
			bump(counts[key], value);
		}
	}
}

/**
 * One pass yields the filtered rows and every facet's counts.
 *
 * A count for option V in facet F is the number of rows satisfying every active
 * facet EXCEPT F, that also carry V. Self-exclusion is unconditional, including
 * for single-select facets: a count answers "how many results if I pick this?",
 * and picking a new value on a single-select facet REPLACES the current one
 * rather than widening it. Self-including would instead compute
 * `(other facets) ∩ current ∩ candidate`, which is zero for every option except
 * the one already selected - every other option then trips the disable-at-zero
 * rule and the facet locks itself, unable to switch without clearing first.
 *
 * `base` gates rows before any counting, so it must be applied for counts to agree
 * with the grid. It is accepted here to keep that ordering guaranteed, but note that
 * it is re-evaluated per row on every call. A caller whose base predicate is
 * expensive (a fuzzy match, say) and who recomputes the tally on selection changes
 * should pre-filter the rows in its own memoized step and omit this argument — see
 * FilterStateService.getTally, which does exactly that.
 */
export function tallyFacets<TRow>(
	rows: readonly TRow[],
	groups: readonly FacetGroup<TRow>[],
	selections: FacetSelections,
	base?: ((row: TRow) => boolean) | null
): TallyResult<TRow> {
	const counts: { [filterKey: string]: Map<number, number> } = {};
	for (const group of groups) {
		for (const key of facetKeys(group)) {
			counts[key] = new Map<number, number>();
		}
	}

	// JS shift counts are taken mod 32, so at 32 groups `1 << 32 === 1` and every mask
	// comparison below silently breaks (`all` becomes 0, so only rows matching NO group
	// would pass) instead of throwing. No real screen approaches this, so fail loudly.
	if (groups.length >= 32) {
		throw new Error(`tallyFacets supports at most 31 facet groups; received ${groups.length}.`);
	}

	const all = (1 << groups.length) - 1;
	const selfExclude = groups.map((_, i) => 1 << i);
	const visible: TRow[] = [];

	for (const row of rows) {
		if (base && !base(row)) {
			continue;
		}

		let mask = 0;
		for (let i = 0; i < groups.length; i++) {
			if (groupMatches(groups[i], row, selections)) {
				mask |= 1 << i;
			}
		}

		for (let i = 0; i < groups.length; i++) {
			if ((mask | selfExclude[i]) === all) {
				bumpGroup(groups[i], row, selections, counts);
			}
		}

		if (mask === all) {
			visible.push(row);
		}
	}

	return { visible, counts };
}
